import Cookies from 'js-cookie';

export const TOKEN_TYPE = {
  ACCESS_TOKEN: 'sso_access_token',
  REFRESH_TOKEN: 'sso_refresh_token',
  EXPIRES_AT: 'sso_token_expires_at',
  GOOGLE_ACCESS_TOKEN: 'googleDriveAccessToken',
  GOOGLE_TOKEN_EXPIRES_AT: 'googleDriveTokenExpiresAt',
} as const;

export type TokenType = typeof TOKEN_TYPE[keyof typeof TOKEN_TYPE];

// Token management functions
export const setTokenToCookies = (
  tokenType: TokenType,
  token: string,
  expiresInSeconds?: number,
  domain?: string,
): void => {
  const options: Cookies.CookieAttributes = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
  };
  if (domain) options.domain = domain;

  if (expiresInSeconds) {
    options.expires = new Date(Date.now() + expiresInSeconds * 1000);
  } else {
    // Default expiry: 1 hour for access token, 30 days for refresh token
    const defaultDays = tokenType === TOKEN_TYPE.ACCESS_TOKEN ? 1/24 : 30;
    options.expires = defaultDays;
  }

  Cookies.set(tokenType, token, options);
};

export const getTokenFromCookies = (tokenType: TokenType): string | null => {
  return Cookies.get(tokenType) || null;
};

export const removeTokenFromCookies = (tokenType: TokenType, domain?: string): void => {
  Cookies.remove(tokenType, { path: '/', ...(domain ? { domain } : {}) });
};

export const clearAllTokens = (domain?: string): void => {
  removeTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN, domain);
  removeTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN, domain);
  removeTokenFromCookies(TOKEN_TYPE.EXPIRES_AT, domain);
  removeTokenFromCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN, domain);
  removeTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT, domain);
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
};

// Get all auth tokens
export const getAuthTokens = () => {
  return {
    accessToken: getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN),
    refreshToken: getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN),
    expiresAt: getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT),
  };
};

// Check if token is expired or will expire soon (30 seconds buffer)
export const isTokenExpiring = (): boolean => {
  const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT);
  if (!expiresAt) return true;

  const expireTime = new Date(expiresAt);
  const now = new Date();
  const bufferTime = new Date(now.getTime() + 30 * 1000); // 30 seconds buffer

  return bufferTime >= expireTime;
};

// Set token expiry time
export const setTokenExpiry = (expiresAt: string, domain?: string): void => {
  setTokenToCookies(TOKEN_TYPE.EXPIRES_AT, expiresAt, undefined, domain);
};

// Google Drive token management
// Note: Refresh token is stored on backend, only access token is stored in cookies
export const setGoogleAccessToken = (token: string, expiresInSeconds: number): void => {
  if (!token || expiresInSeconds <= 0) {
    // Clear tokens if empty
    removeTokenFromCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN);
    removeTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT);
    return;
  }

  // Save access token
  setTokenToCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN, token, expiresInSeconds);

  // Save expiry timestamp
  const expiresAt = new Date(Date.now() + expiresInSeconds * 1000).toISOString();
  setTokenToCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT, expiresAt, expiresInSeconds);
};

export const getGoogleAccessToken = (): string | null => {
  const token = getTokenFromCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN);
  const expiresAt = getTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT);

  if (!token || !expiresAt) return null;

  // Check if expired
  const expireTime = new Date(expiresAt);
  const now = new Date();

  if (now >= expireTime) {
    // Token expired, clear it
    removeTokenFromCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN);
    removeTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT);
    return null;
  }

  return token;
};

/**
 * Check if Google token is expiring soon (within 5 minutes)
 * Used to trigger silent token refresh
 */
export const isGoogleTokenExpiring = (): boolean => {
  const expiresAt = getTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT);

  if (!expiresAt) return true;

  const expireTime = new Date(expiresAt);
  const now = new Date();
  const bufferTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes buffer

  return bufferTime >= expireTime;
};

export const clearGoogleAccessToken = (): void => {
  removeTokenFromCookies(TOKEN_TYPE.GOOGLE_ACCESS_TOKEN);
  removeTokenFromCookies(TOKEN_TYPE.GOOGLE_TOKEN_EXPIRES_AT);
};

export const hasGoogleDriveAccess = (): boolean => {
  return !!getGoogleAccessToken();
};
